export const MAX_LABEL_LENGTH = 40;

export const CHIP_GAP_PX = 8;
const COUNT_CHIP_BUFFER_PX = 2;

// MUI small Chip's combined label padding + border, from @mui/material/Chip/Chip.js:
// filled = 8px+8px label padding + 0 border = 16px; outlined = 7px+7px label padding + 1px+1px border = 16px.
// Both variants land on the same total, so one constant covers either.
const SMALL_CHIP_OVERHEAD_PX = 16;
const CHIP_FONT = '13px Roboto, Helvetica, Arial, sans-serif';

let measureContext: CanvasRenderingContext2D | null | undefined;

function getMeasureContext(): CanvasRenderingContext2D | null {
  if (measureContext === undefined) {
    measureContext = document.createElement('canvas').getContext('2d');
    if (measureContext) {
      measureContext.font = CHIP_FONT;
    }
  }
  return measureContext;
}

// Estimates a rendered small Chip's width without mounting any DOM, by measuring the
// label text via Canvas 2D and adding MUI's known label padding/border overhead.
// `maxLength` mirrors the `maxWidth: ${MAX_LABEL_LENGTH}ch` cap applied to label chips.
export function measureChipWidth(text: string, maxLength?: number): number {
  const ctx = getMeasureContext();
  if (!ctx) {
    return 0;
  }

  const width = ctx.measureText(text).width + SMALL_CHIP_OVERHEAD_PX;
  if (maxLength === undefined) {
    return width;
  }

  const chWidth = ctx.measureText('0').width;
  return Math.min(width, chWidth * maxLength + SMALL_CHIP_OVERHEAD_PX);
}

export interface LabelLayoutResult {
  readonly visibleCount: number;
  readonly truncatedLabelMaxWidth?: number;
}

export function sumLabelWidths(
  chipWidths: number[],
  visibleCount: number,
  gap: number
): number {
  let total = 0;
  for (let index = 0; index < visibleCount; index++) {
    if (index > 0) {
      total += gap;
    }
    total += chipWidths[index];
  }
  return total;
}

export function getLabelLayout(
  chipWidths: number[],
  containerWidth: number,
  countChipWidths: ReadonlyMap<number, number>,
  gap: number
): LabelLayoutResult {
  const total = chipWidths.length;

  if (total <= 1) {
    return { visibleCount: total };
  }

  const allLabelsWidth = sumLabelWidths(chipWidths, total, gap);
  if (allLabelsWidth <= containerWidth) {
    return { visibleCount: total };
  }

  for (let visibleCount = total - 1; visibleCount >= 2; visibleCount--) {
    const hiddenCount = total - visibleCount;
    const countChipWidth =
      (countChipWidths.get(hiddenCount) ?? 0) + COUNT_CHIP_BUFFER_PX;
    const labelAreaWidth = containerWidth - countChipWidth - gap;
    const naturalLabelWidth = sumLabelWidths(chipWidths, visibleCount, gap);

    if (naturalLabelWidth <= labelAreaWidth) {
      return { visibleCount };
    }
  }

  const hiddenCount = total - 1;
  const countChipWidth =
    (countChipWidths.get(hiddenCount) ?? 0) + COUNT_CHIP_BUFFER_PX;

  return {
    visibleCount: 1,
    truncatedLabelMaxWidth: getTruncatedLabelMaxWidth(
      containerWidth,
      countChipWidth,
      gap
    ),
  };
}

export function getTruncatedLabelMaxWidth(
  containerWidth: number,
  countChipWidth: number,
  gap: number
): number {
  return Math.max(0, containerWidth - countChipWidth - gap);
}
